// One-shot status messages, primarily surfaced via URL query params after
// server redirects (e.g. /account?status=email-changed). The PageShell
// reads the URL on mount, calls fromQuery(), then strips the param via
// history.replaceState so a refresh doesn't re-fire the toast.
//
// Toasts auto-dismiss after DEFAULT_TTL_MS unless `ttlMs: 0` is passed.

import { writable } from 'svelte/store';

export type ToastVariant = 'info' | 'success' | 'warn' | 'error';

export interface Toast {
  id: number;
  variant: ToastVariant;
  message: string;
  ttlMs: number;
}

const DEFAULT_TTL_MS = 5000;

export const toasts = writable<Toast[]>([]);

let nextId = 1;
const timeouts = new Map<number, ReturnType<typeof setTimeout>>();

export function showToast(message: string, variant: ToastVariant = 'info', ttlMs = DEFAULT_TTL_MS): number {
  const id = nextId++;
  const toast: Toast = { id, variant, message, ttlMs };
  toasts.update((list) => [...list, toast]);

  if (ttlMs > 0) {
    timeouts.set(
      id,
      setTimeout(() => dismissToast(id), ttlMs),
    );
  }

  return id;
}

export function dismissToast(id: number): void {
  const t = timeouts.get(id);
  if (t) {
    clearTimeout(t);
    timeouts.delete(id);
  }
  toasts.update((list) => list.filter((toast) => toast.id !== id));
}

/**
 * Map common server-redirect query params to toast messages. Mirrors the
 * status/error vocabulary used by EmailChangeController, AccountRestore,
 * GoogleController, etc. Unknown values are silently ignored — this lets
 * us add new status codes server-side without breaking older SPA builds.
 */
const STATUS_MESSAGES: Record<string, [ToastVariant, string]> = {
  verified: ['success', 'Your email is verified.'],
  'email-changed': ['success', 'Email address updated.'],
  restored: ['success', 'Welcome back — your account has been restored.'],
  'already-verified': ['info', 'Your email was already verified.'],
};

const ERROR_MESSAGES: Record<string, [ToastVariant, string]> = {
  oauth_failed: ['error', "Couldn't sign in with Google. Try again or use email."],
  oauth_canceled: ['info', 'Google sign-in was canceled.'],
  link_expired: ['warn', 'That link has expired. Request a new one.'],
  link_invalid: ['error', 'That link is no longer valid.'],
  email_taken: ['error', 'That email is already in use.'],
};

/**
 * Read ?status=… / ?error=… from the URL, fire matching toasts, and
 * return a cleaned URL with those params stripped. Caller is responsible
 * for replacing history state with the cleaned URL.
 */
export function fromQuery(url: URL): URL {
  const cleaned = new URL(url.toString());
  const status = cleaned.searchParams.get('status');
  const error = cleaned.searchParams.get('error');

  if (status && STATUS_MESSAGES[status]) {
    const [variant, message] = STATUS_MESSAGES[status];
    showToast(message, variant);
  }

  if (error && ERROR_MESSAGES[error]) {
    const [variant, message] = ERROR_MESSAGES[error];
    showToast(message, variant);
  }

  cleaned.searchParams.delete('status');
  cleaned.searchParams.delete('error');
  return cleaned;
}
